/**
 * «Удалить» — `POST …/discard {comment?}`.
 *
 * ЧТО ЗДЕСЬ ОБЯЗАНО БЫТЬ СКАЗАНО. Кнопка называется «Удалить», а удаления не происходит:
 * черновик уходит в терминальное состояние «отброшен», строка остаётся в базе, решение и
 * переход ложатся в журнал, который живёт не меньше полугода (NFR-2). Человек имеет право
 * знать обе половины — и что пост из бэклога пропадёт, и что запись о нём никуда не денется.
 * Поэтому последствие стоит одной фразой над кнопкой, а не мелким шрифтом под ней.
 *
 * Удержания (`HoldToConfirm`), как на утверждении и отзыве, здесь нет намеренно: те два
 * действия видит канал — подписчики получают пост или замечают его пропажу. Отбрасывание
 * не выходит за пределы бэклога, и цена ошибки — разговор с автором, а не публикация.
 * Подтверждения диалогом для этого достаточно.
 *
 * Комментарий необязателен — так объявлен и маршрут (`{comment?}`). Требовать причину
 * значило бы получать «не надо» в половине строк журнала.
 */

import { postLabel } from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type { DiscardResponse, RenderModel } from '../../api/types.ts';
import { Modal } from '../../components/Modal.tsx';

const COMMENT_MAX = 2000;

export interface DiscardDialogProps {
  readonly model: RenderModel;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

export function DiscardDialog({ model, onChanged, onClose }: DiscardDialogProps): JSX.Element {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = comment.trim();

  const submit = (): void => {
    if (busy || trimmed.length > COMMENT_MAX) return;
    setBusy(true);
    setError(null);
    post<DiscardResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/discard`,
      trimmed === '' ? {} : { comment: trimmed },
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 409) {
          // 409 здесь означает две разные вещи — состояние уехало или черновик занимает
          // слот, — и текст бэкенда различает их сам. Пересказывать его своими словами
          // значит потерять вторую половину: «сначала снимите со слота» это инструкция.
          setError(`${e.message}. Обновите экран.`);
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Удалить из бэклога"
      subtitle={`${postLabel(model.post_no)} · v${model.version_no}`}
      onClose={onClose}
    >
      <div className="val val-warn">
        <span>
          Черновик уйдёт из бэклога и вернуть его будет нельзя — история останется в журнале.
        </span>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <label className="lbl" htmlFor="discard-comment" style={{ margin: 0 }}>
          Почему удаляем — необязательно, попадёт в журнал
        </label>
        <textarea
          id="discard-comment"
          className="inp"
          rows={3}
          maxLength={COMMENT_MAX}
          value={comment}
          placeholder="Например: повод протух, новость никому не интересна."
          onChange={(e) => {
            setComment(e.target.value);
          }}
        />
      </div>

      <div className="notice">
        Строка черновика из базы не удаляется: на неё ссылается журнал аудита, а он хранится не
        менее шести месяцев. Удалённые посты остаются видны в бэклоге под фильтром «Архив».
      </div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <button
          type="button"
          className={busy ? 'btn btn-w btn-dis' : 'btn btn-pri btn-w'}
          disabled={busy}
          onClick={submit}
        >
          Удалить из бэклога
        </button>
        <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  );
}
